import { Button, ButtonProps } from 'Atoms';
import { CashSovereignsOptions, PaiPortfolioReport, PortfolioCompany } from 'models';
import { DownloadIcon } from 'Tokens/Icons/Function';
import {
  PaiResults,
  ReportPeriods,
  mapUnitToCompanyCurrencyPAI,
  usePaiReportAnswers,
} from './PortfolioPai.hooks';
import ExcelJS from 'exceljs';
import { calculateMetricResults, getMetricProgress } from 'utils/scores/pai';
import { saveAs } from 'file-saver';
import { useParams } from 'react-router-dom';
import usePortfolios, { useCashAndSovereigns } from 'containers/Portfolios/Portfolios.hooks';
import { useCurrentCompany } from 'utils/hooks';
import { splitQuarterYear } from 'utils/date';
import { sortTable1Indicators } from './PaiIndicatorsModal';

export const PaiExport = ({
  selectedIndicators,
  results,
  reportingYear,
  cashAndSovereigns,
  portfolioCompanies,
  onClick,
  sovereignRowSelected,
  cashRowSelected,
}: {
  selectedIndicators: PaiPortfolioReport['selectedIndicators'];
  results: PaiResults | undefined;
  reportingYear: string;
  cashAndSovereigns: CashSovereignsOptions;
  portfolioCompanies: PortfolioCompany[];
  onClick?: ButtonProps['onClick'];
  sovereignRowSelected?: boolean;
  cashRowSelected?: boolean;
}) => {
  const { portfolioId } = useParams();
  const { getPortfolio } = usePortfolios();
  const [, year] = splitQuarterYear(reportingYear);

  const companies = results?.map((result) => result.company);

  const resultsQ1 = usePaiReportAnswers(portfolioId ?? '', Number(year), ReportPeriods.q1).results;
  const resultsQ2 = usePaiReportAnswers(portfolioId ?? '', Number(year), ReportPeriods.q2).results;
  const resultsQ3 = usePaiReportAnswers(portfolioId ?? '', Number(year), ReportPeriods.q3).results;
  const resultsQ4 = usePaiReportAnswers(portfolioId ?? '', Number(year), ReportPeriods.q4).results;
  const resultsYear = usePaiReportAnswers(
    portfolioId ?? '',
    Number(year),
    ReportPeriods.year
  ).results;

  const { cash: cashQ1, sovereigns: sovQ1 } = useCashAndSovereigns({
    portfolioId,
    yearAndQuarter: reportingYear,
    reportPeriod: ReportPeriods.q1,
  });
  const { cash: cashQ2, sovereigns: sovQ2 } = useCashAndSovereigns({
    portfolioId,
    yearAndQuarter: reportingYear,
    reportPeriod: ReportPeriods.q2,
  });
  const { cash: cashQ3, sovereigns: sovQ3 } = useCashAndSovereigns({
    portfolioId,
    yearAndQuarter: reportingYear,
    reportPeriod: ReportPeriods.q3,
  });
  const { cash: cashQ4, sovereigns: sovQ4 } = useCashAndSovereigns({
    portfolioId,
    yearAndQuarter: reportingYear,
    reportPeriod: ReportPeriods.q4,
  });
  const { cash: cashYear, sovereigns: sovYear } = useCashAndSovereigns({
    portfolioId,
    yearAndQuarter: reportingYear,
    reportPeriod: ReportPeriods.year,
  });

  const { company } = useCurrentCompany();

  const sortedIndicators = sortTable1Indicators(selectedIndicators);

  const portfolio = getPortfolio(portfolioId ?? '', reportingYear);
  const investorMetricsList = sortedIndicators
    .map((indicator) =>
      indicator.indicator.investorMetrics.map((investorMetric) => ({
        ...investorMetric.investorMetric,
        indicator: indicator.indicator,
        companyCount:
          (indicator.isForAllCompanies
            ? results?.length
            : indicator.paiReportIndicatorPortfolioCompanies.length) ?? 1,
      }))
    )
    .flat();

  const investeeMetricsList = sortedIndicators
    .map((indicator) =>
      indicator.indicator.investorMetrics.map((investorMetric) => ({
        ...investorMetric.investorMetric,
        indicator: indicator.indicator,
      }))
    )
    .flat()
    .map((investorMetric) =>
      investorMetric.investeeMetrics.map((investeeMetric) => ({
        ...investeeMetric.metric,
        investorMetric: investorMetric,
      }))
    )
    .flat();

  const onExport = () => {
    const workbook = new ExcelJS.Workbook();
    const overviewSheet = workbook.addWorksheet('Portfolio_overview');

    overviewSheet.columns = [
      { key: 'company', header: 'Company', width: 25 },
      { key: 'marketValue', header: 'Enterprise value', width: 15 },
      { key: 'investmentQ1', header: 'Value of investment Q1', width: 15 },
      { key: 'investmentQ2', header: 'Value of investment Q2', width: 15 },
      { key: 'investmentQ3', header: 'Value of investment Q3', width: 15 },
      { key: 'investmentQ4', header: 'Value of investment Q4', width: 15 },
      { key: 'year', header: 'Value of investment - Year', width: 15 },
      { key: 'included', header: 'Included in the calculation of the exported results', width: 25 },
    ];

    const description =
      'This sheet provides an overview of the portfolio companies included in the calculation and any cash or sovereigns if added.';
    overviewSheet.spliceRows(1, 0, [description]);

    overviewSheet.mergeCells(1, 1, 1, overviewSheet.columns.length);

    const descriptionRow = overviewSheet.getRow(1);
    descriptionRow.font = { bold: true };
    descriptionRow.alignment = { horizontal: 'center' };

    overviewSheet.spliceRows(2, 0, [], []);

    const overviewRows = portfolioCompanies.map((company) => {
      const companyQ1Results = resultsQ1?.find(
        (result) => result.company.id === company.company?.id
      );
      const companyQ2Results = resultsQ2?.find(
        (result) => result.company.id === company.company?.id
      );
      const companyQ3Results = resultsQ3?.find(
        (result) => result.company.id === company.company?.id
      );
      const companyQ4Results = resultsQ4?.find(
        (result) => result.company.id === company.company?.id
      );
      const companyYearResults = resultsYear?.find(
        (result) => result.company.id === company.company?.id
      );
      return {
        company: company.company?.name,
        marketValue: companyYearResults?.marketValue ?? 'N/A',
        investmentQ1: companyQ1Results?.allocation ?? 'N/A',
        investmentQ2: companyQ2Results?.allocation ?? 'N/A',
        investmentQ3: companyQ3Results?.allocation ?? 'N/A',
        investmentQ4: companyQ4Results?.allocation ?? 'N/A',
        year: companyYearResults?.allocation ?? 'N/A',
        included: !!companies?.find((c) => c.id === company.company?.id) ? 'Yes' : 'No',
      };
    });

    if (cashRowSelected) {
      overviewRows.push({
        company: 'Cash',
        marketValue: 'N/A',
        investmentQ1: cashQ1,
        investmentQ2: cashQ2,
        investmentQ3: cashQ3,
        investmentQ4: cashQ4,
        year: cashYear,
        included: cashAndSovereigns?.cash === 0 ? 'No' : 'Yes',
      });
    }
    if (sovereignRowSelected) {
      overviewRows.push({
        company: 'Sovereigns',
        marketValue: 'N/A',
        investmentQ1: sovQ1,
        investmentQ2: sovQ2,
        investmentQ3: sovQ3,
        investmentQ4: sovQ4,
        year: sovYear,
        included: cashAndSovereigns?.sovereigns === 0 ? 'No' : 'Yes',
      });
    }
    overviewSheet.addRows(overviewRows);

    const paiResultsSheet = workbook.addWorksheet('Pai Results');
    paiResultsSheet.columns = [
      {
        key: 'indicator',
        header: 'Indicator',
        width: 50,
      },
      {
        key: 'metric',
        header: 'Metric',
        width: 100,
      },
      {
        key: 'impactValue',
        header: 'Impact this year',
        width: 25,
      },
      {
        key: 'impactUnit',
        header: 'Unit',
        width: 15,
      },
      {
        key: 'answers',
        header: 'Answers collected',
        width: 20,
      },
    ];

    const rows = investorMetricsList.map((investorMetric) => {
      const metricResults = calculateMetricResults(
        investorMetric,
        results ?? [],
        cashAndSovereigns
      );
      const impactValue = metricResults.result ?? 'N/A';
      const impactUnit = mapUnitToCompanyCurrencyPAI(investorMetric.unit, company?.currency) ?? '';

      const metricProgress = getMetricProgress(
        investorMetric,
        results ?? [],
        investorMetric.companyCount
      );
      const formattedProgress =
        metricProgress !== null && metricProgress !== undefined ? `${metricProgress}%` : '0%';
      return {
        indicator: investorMetric.indicator?.title ?? 'N/A',
        metric: investorMetric.title ?? 'N/A',
        impactValue: impactValue,
        impactUnit: impactUnit,
        answers: formattedProgress,
      };
    });

    const removeIllegalCharacters = (name: string): string => {
      return name.replace(/[\*\?\:\[\]\/\\]/g, '_');
    };

    paiResultsSheet.addRows(rows);

    results?.forEach((res) => {
      const formatedCompanyName = removeIllegalCharacters(res.company.name);
      const companySheet = workbook.addWorksheet(`${formatedCompanyName}-raw-results`);
      companySheet.columns = [
        {
          key: 'indicator',
          header: 'Indicator',
          width: 100,
        },
        {
          key: 'metric',
          header: 'Metric',
          width: 100,
        },
        {
          key: 'question',
          header: 'Question',
          width: 100,
        },
        {
          key: 'answerValue',
          header: 'Answer',
          width: 20,
        },
        {
          key: 'answerUnit',
          header: 'Unit',
          width: 15,
        },
        {
          key: 'note',
          header: 'Note',
          width: 20,
        },
        {
          key: 'marketValue',
          header: 'Enterprise value',
          width: 20,
        },
        {
          key: 'allocation',
          header: 'Value of investment',
          width: 20,
        },
      ];
      const companyRows = investeeMetricsList.map((investeeMetric) => {
        let answerValue = 'N/A';
        let answerUnit = '';

        if (investeeMetric?.reference && res.answers[investeeMetric.reference] !== undefined) {
          const rawAnswer = `${res.answers[investeeMetric.reference]}`;
          const unit = investeeMetric.unit ?? '';
          answerValue = rawAnswer;
          answerUnit = unit;
        }

        return {
          answerValue: answerValue,
          answerUnit: answerUnit,
          indicator: investeeMetric.investorMetric.indicator?.title ?? 'N/A',
          metric: investeeMetric.investorMetric.title ?? 'N/A',
          question: investeeMetric.title ?? 'N/A',
          note:
            investeeMetric?.reference && !!res.notes[investeeMetric.reference]
              ? `${res.notes[investeeMetric.reference]}`
              : '',
          marketValue: res.marketValue ?? '0',
          allocation: res.allocation ?? '0',
        };
      });
      companySheet.addRows(companyRows);
    });

    workbook.xlsx.writeBuffer().then(function (buffer) {
      // done
      console.log(buffer);

      const blob = new Blob([buffer], { type: 'applicationi/xlsx' });
      saveAs(blob, `${portfolio?.name}-pai-results.xlsx`);
    });
  };

  return (
    <Button
      onClick={(e) => {
        onExport();
        if (onClick) {
          onClick(e);
        }
      }}
      // isLoading={loading}
      leftIcon={<DownloadIcon color="inherit" />}
      variant="primary"
      alignSelf="start"
    >
      Export results
    </Button>
  );
};
